import { Qualification } from "./qualification/Qualification";
import { Registration } from "./pages/Registration";
import { localFixtureHost } from "./config/capabilities";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { Login } from "./pages/Login";
import { WorkspaceView } from "./pages/Workspace";
import { ApiError, currentAdmin, gateway, readRecoveryDelay, signOut, switchOrganization } from "./api/client";
import type { Admin } from "./domain";
import { landing } from "./auth/policy";
import { Brand } from "./components/Brand";
import { useLocale } from "./i18n";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
const Preview =
  __MZN_PREVIEW__ && localFixtureHost(location.hostname)
    ? lazy(() => import("./pages/Preview"))
    : null;
export default function App() {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [checking, setChecking] = useState(
    !location.pathname.startsWith("/preview") && !["/qualification", "/register"].includes(location.pathname),
  );
  const navigate = useNavigate();
  const [sessionError, setSessionError] = useState<unknown>(null);
  const sessionUnavailable = Boolean(sessionError);
  const recoveryAttempts = useRef(0);
  const [retry, setRetry] = useState(0);
  const authRevision = useRef(0);
  const changingOrganization = useRef(false);
  const [switching, setSwitching] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    const initialRevision = authRevision.current;
    setSessionError(null);
    if (!location.pathname.startsWith("/preview") && !["/qualification", "/register"].includes(location.pathname)) {
      currentAdmin(controller.signal)
        .then((a) => {
          if (!controller.signal.aborted && initialRevision === authRevision.current) { recoveryAttempts.current = 0; setAdmin(a); }
        })
        .catch((error) => {
          if (!controller.signal.aborted && initialRevision === authRevision.current && !sessionDenied(error)) setSessionError(error);
        })
        .finally(() => {
          if (!controller.signal.aborted) setChecking(false);
        });
    }
    const expired = () => {
      if (["/qualification", "/register"].includes(location.pathname)) return;
      authRevision.current++;
      setSessionError(null);
      setAdmin(null);
      navigate("/login", { replace: true });
    };
    let refreshing = false;
    const revalidate = () => {
      if (location.pathname.startsWith("/preview") || ["/login", "/qualification", "/register"].includes(location.pathname) || document.hidden || refreshing || changingOrganization.current) return;
      refreshing = true;
      const revision = authRevision.current;
      void currentAdmin(controller.signal).then((next) => {
        if (!controller.signal.aborted && !changingOrganization.current && revision === authRevision.current) { recoveryAttempts.current = 0; setAdmin(next); setSessionError(null); }
      }).catch((error) => { if (!controller.signal.aborted && !changingOrganization.current && revision === authRevision.current && error?.name !== "AbortError") { if (sessionDenied(error)) expired(); else setSessionError(error); } }).finally(() => { refreshing = false; });
    };
    const timer = window.setInterval(revalidate, 60_000);
    window.addEventListener("focus", revalidate);
    window.addEventListener("online", revalidate);
    window.addEventListener("mzn:access-check", revalidate);
    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("mzn:session-expired", expired);
    return () => {
      controller.abort();
      clearInterval(timer);
      window.removeEventListener("focus", revalidate);
      window.removeEventListener("online", revalidate);
      window.removeEventListener("mzn:access-check", revalidate);
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("mzn:session-expired", expired);
    };
  }, [navigate, retry]);
  useEffect(() => {
    if (!sessionError) return;
    const delay = readRecoveryDelay(sessionError, recoveryAttempts.current);
    if (delay === undefined) return;
    const timer = window.setTimeout(() => {
      recoveryAttempts.current++;
      if (admin) window.dispatchEvent(new Event("mzn:access-check"));
      else { setChecking(true); setRetry(value => value + 1); }
    }, delay);
    return () => clearTimeout(timer);
  }, [sessionError, admin]);
  async function changeOrganization(id: string) {
    if (changingOrganization.current || id === admin?.organization?.id) return;
    changingOrganization.current = true;
    setSwitching(true);
    try {
      const next = await switchOrganization(id);
      setAdmin(next);
      navigate(landing(next), { replace: true });
    } finally {
      changingOrganization.current = false;
      setSwitching(false);
    }
  }
  async function logout() {
    authRevision.current++;
    await signOut();
    setAdmin(null);
    navigate("/login", { replace: true });
  }
  const retrySession = () => { setChecking(true); setRetry(value => value + 1); };
  if (sessionUnavailable && !admin) return <SessionUnavailable onRetry={retrySession} />;
  return (
    <>
    {sessionUnavailable && admin && <SessionUnavailable compact onRetry={() => window.dispatchEvent(new Event("mzn:access-check"))} />}
    <Routes>
      <Route path="/qualification" element={<Qualification />} />
      <Route path="/register" element={<Registration />} />
      <Route
        path="/login"
        element={
          admin ? (
            <Navigate to={landing(admin)} replace />
          ) : (
            <Login
              checking={checking}
              onLogin={(a) => {
                authRevision.current++;
                recoveryAttempts.current = 0;
                setSessionError(null);
                setAdmin(a);
                navigate(landing(a), { replace: true });
              }}
            />
          )
        }
      />
      <Route
        path="/app/*"
        element={
          checking ? (
            <WorkspaceLoading />
          ) : admin ? (
            <WorkspaceView key={`${admin.id}:${admin.organization?.id}:${admin.scope}`} admin={admin} gateway={gateway} onSignOut={logout} onOrganizationChange={changeOrganization} switchingOrganization={switching} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      {Preview && (
        <Route
          path="/preview/*"
          element={
            <Suspense fallback={<WorkspaceLoading />}>
              <Preview />
            </Suspense>
          }
        />
      )}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}
function WorkspaceLoading() {
  const {w} = useLocale();
  return <div className="workspace-loading" role="status"><Brand /><p><ArrowPathIcon className="spin" />{w("جارٍ تجهيز مساحة عملك…", "Preparing your workspace…")}</p></div>;
}

function sessionDenied(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}
function SessionUnavailable({ onRetry, compact = false }: { onRetry: () => void; compact?: boolean }) {
  const { w } = useLocale();
  return <div className={compact ? "session-connection-notice" : "workspace-loading"} role="alert">
    {!compact && <Brand />}
    <p>{w("تعذّر التحقق من الجلسة بسبب مشكلة في الاتصال. أعد المحاولة.", "Unable to check your session because of a connection problem. Please retry.")}</p>
    <button type="button" onClick={onRetry}>{w("إعادة المحاولة", "Retry")}</button>
  </div>;
}
